import { useState } from "react";
import { Send } from "lucide-react";
import { requestAffiliatePayout } from "wasp/client/operations";
import { Badge } from "../../client/components/ui/badge";
import { Button } from "../../client/components/ui/button";
import { Card, CardContent } from "../../client/components/ui/card";
import { CardSectionHeader } from "../../client/components/ui/card-section-header";
import { useToast } from "../../client/hooks/use-toast";
import useTranslation from "../../client/hooks/useTranslation";
import type { Translations } from "../../client/i18n/translations";
import { formatEur } from "./formatEur";

interface CommissionRow {
  id: string;
  createdAt: Date | string;
  paymentAmount: number;
  rateApplied: number;
  commissionAmount: number;
  status: string;
  referredEmailMasked: string;
}

interface CommissionsSectionProps {
  balances: { pending: number; confirmed: number; totalPaid: number };
  commissions: CommissionRow[];
  canRequestPayout: boolean;
  minPayoutAmount: number;
  hasPayoutDetails: boolean;
  isDark: boolean;
}

export function commissionStatusLabel(
  t: Translations,
  status: string,
): string {
  switch (status) {
    case "pending":
      return t.affiliate.status.pending;
    case "confirmed":
      return t.affiliate.status.confirmed;
    case "cancelled":
      return t.affiliate.status.cancelled;
    case "paid":
      return t.affiliate.status.paid;
    default:
      return status;
  }
}

function statusBadgeVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "confirmed":
      return "default";
    case "paid":
      return "secondary";
    case "cancelled":
      return "destructive";
    default:
      return "outline";
  }
}

export default function CommissionsSection({
  balances,
  commissions,
  canRequestPayout,
  minPayoutAmount,
  hasPayoutDetails,
  isDark,
}: CommissionsSectionProps) {
  const t = useTranslation();
  const { toast } = useToast();
  const [isRequesting, setIsRequesting] = useState(false);

  const handleRequestPayout = async () => {
    setIsRequesting(true);
    try {
      await requestAffiliatePayout({});
      toast({ description: t.affiliate.payout.requestSuccess });
    } catch (err: any) {
      toast({
        variant: "destructive",
        description: err?.message || t.affiliate.payout.requestError,
      });
    } finally {
      setIsRequesting(false);
    }
  };

  const balanceItems = [
    { label: t.affiliate.balances.pending, value: balances.pending },
    { label: t.affiliate.balances.confirmed, value: balances.confirmed },
    { label: t.affiliate.balances.totalPaid, value: balances.totalPaid },
  ];

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.affiliate.commissions.title}
        isDark={isDark}
        compact
        rightContent={
          <Button
            size="sm"
            disabled={!canRequestPayout || !hasPayoutDetails || isRequesting}
            onClick={handleRequestPayout}
          >
            <Send className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            {t.affiliate.payout.requestButton}
          </Button>
        }
      />
      <CardContent className="px-4 pb-4 pt-3 flex-1 flex flex-col gap-4">
        <div className="grid grid-cols-3 gap-3">
          {balanceItems.map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-border bg-card p-3"
            >
              <p className="widget-label text-[10px] uppercase tracking-wider">
                {item.label}
              </p>
              <p className="mt-1 text-lg font-bold font-mono tabular-nums text-foreground">
                {formatEur(item.value)}
              </p>
            </div>
          ))}
        </div>

        {!canRequestPayout && (
          <p className="text-xs text-muted-foreground">
            {t.affiliate.payout.requestHint.replace(
              "{min}",
              String(minPayoutAmount),
            )}
          </p>
        )}
        {canRequestPayout && !hasPayoutDetails && (
          <p className="text-xs text-muted-foreground">
            {t.affiliate.payout.detailsMissing}
          </p>
        )}

        {commissions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {t.affiliate.commissions.empty}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="widget-label py-2 pr-3 font-medium text-xs uppercase tracking-wider">
                    {t.affiliate.commissions.colDate}
                  </th>
                  <th className="widget-label py-2 pr-3 font-medium text-xs uppercase tracking-wider">
                    {t.affiliate.commissions.colClient}
                  </th>
                  <th className="widget-label py-2 pr-3 font-medium text-xs uppercase tracking-wider text-right">
                    {t.affiliate.commissions.colPayment}
                  </th>
                  <th className="widget-label py-2 pr-3 font-medium text-xs uppercase tracking-wider text-right">
                    {t.affiliate.commissions.colRate}
                  </th>
                  <th className="widget-label py-2 pr-3 font-medium text-xs uppercase tracking-wider text-right">
                    {t.affiliate.commissions.colCommission}
                  </th>
                  <th className="widget-label py-2 font-medium text-xs uppercase tracking-wider">
                    {t.affiliate.commissions.colStatus}
                  </th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.id} className="border-b border-border/50">
                    <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
                      {new Date(c.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {c.referredEmailMasked}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">
                      {formatEur(c.paymentAmount)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">
                      {Math.round(c.rateApplied * 1000) / 10} %
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">
                      {formatEur(c.commissionAmount)}
                    </td>
                    <td className="py-2">
                      <Badge variant={statusBadgeVariant(c.status)}>
                        {commissionStatusLabel(t, c.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
